import { useState, useRef } from 'react'
import axios from '@fs/zion-axios'
import Cache from '@fs/zion-cache'
import { useProgressOverlay, ProgressOverlayStatus, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { baseUrl, sgBaseUrl } from '@fs/zion-config'

// Generate clean filename date: YYYY-MM-DD
const getFilenameDateString = () => new Date().toISOString().split('T')[0]

const imageCache = new Cache('generated-images')
const CACHE_DURATION = 2 * 60 * 1000 // 2 minutes

export const useGenerateImage = () => {
  const [t] = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { createProgressItem, setHeaderMessage, setOnDismiss } = useProgressOverlay()
  const { FAILED, IN_PROGRESS, SUCCESSFUL } = ProgressOverlayStatus
  const cancelOverlay = useOverlay()
  const shouldCancel = useRef()
  const isCanceled = useRef(false)
  const abortController = useRef(new AbortController())

  const generateImage = async ({
    siteName,
    style,
    design,
    personId,
    width,
    height,
    backgroundOpacity = 100,
    hiddenIds = [],
    showAvatars = true,
    showPortraits = true,
  }) => {
    if (isLoading) return

    // Generate cache key from parameters including visibility settings
    const hiddenIdsKey = hiddenIds.length > 0 ? hiddenIds.toSorted((a, b) => a.localeCompare(b)).join(',') : 'none'
    const cacheKey = `${siteName}-${style}-${design}-${personId}-${width}-${height}-${backgroundOpacity}-${hiddenIdsKey}-${showAvatars}-${showPortraits}`

    // Check cache for existing image (within last 2 minutes)
    // Short duration prevents serving stale images after tree modifications
    try {
      const cached = await imageCache.getItem(cacheKey)
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        // Use cached blob for instant download
        const blobUrl = URL.createObjectURL(cached.blob)

        // Open in new tab with security settings
        window.open(blobUrl, '_blank', 'noopener,noreferrer')

        // Also trigger download
        const downloadLink = document.createElement('a')
        downloadLink.href = blobUrl
        downloadLink.download = `${design}-${getFilenameDateString()}.jpg`
        downloadLink.click()

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100)

        // Show success overlay to provide visual feedback
        setHeaderMessage(t('shared.useProgressOverlay.header', 'Tree Designs'))
        const progressItemUpdate = createProgressItem({})
        progressItemUpdate({
          message: `📥 ${t('useGenerateImage.success.description', 'Download complete. Now you can share it with your friends and family.')}`,
          status: SUCCESSFUL,
        })
        return
      }
    } catch (err) {
      console.error('Error accessing image cache:', err)
    }

    setIsLoading(true)
    isCanceled.current = false
    abortController.current = new AbortController()

    // Setup progress overlay
    setHeaderMessage(t('shared.useProgressOverlay.header', 'Tree Designs'))
    setOnDismiss(() => {
      abortController.current.abort()
      isCanceled.current = true
      setIsLoading(false)
    })

    const progressItemUpdate = createProgressItem({}, async () => {
      cancelOverlay.open()

      // Wait for user response from confirmation dialog
      const didCancel = await new Promise((resolve) => {
        shouldCancel.current = resolve
      })

      cancelOverlay.close()

      if (didCancel) {
        abortController.current.abort()
        isCanceled.current = true
        setIsLoading(false)
      }

      return didCancel
    })

    try {
      // Build URL with query parameters including all visibility settings
      const hiddenIdsParam = hiddenIds.length > 0 ? `&hiddenIds=${hiddenIds.join(',')}` : ''
      const printUrl = `${baseUrl}/tree-designs/${siteName}/${style}/${design}/${personId}?buildMode=false&backgroundOpacity=${backgroundOpacity}&showAvatars=${showAvatars}&showPortraits=${showPortraits}${hiddenIdsParam}`

      const requestUrl = new URL(`${sgBaseUrl}/frontier/prerender/apigw/image`)
      requestUrl.searchParams.append('url', printUrl)
      requestUrl.searchParams.append('type', 'jpeg')
      requestUrl.searchParams.append('selector', '#tree-design')
      requestUrl.searchParams.append('width', width)
      requestUrl.searchParams.append('height', height)
      requestUrl.searchParams.append('isAuthRoute', 'true')

      progressItemUpdate({
        message: t('useGenerateImage.loading.description', 'Generating image...'),
        status: IN_PROGRESS,
      })

      const response = await axios.get(requestUrl.toString(), {
        responseType: 'arraybuffer',
        signal: abortController.current.signal,
      })

      // Check for cancellation after request
      if (isCanceled.current) {
        return
      }

      // Create blob and cache it
      const blob = new Blob([response.data], { type: 'image/jpeg' })
      imageCache.setItem(cacheKey, { blob, timestamp: Date.now() })

      // Download the image
      const blobUrl = URL.createObjectURL(blob)

      // Open in new tab with security settings
      window.open(blobUrl, '_blank', 'noopener,noreferrer')

      // Also trigger download
      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = `${design}-${getFilenameDateString()}.jpg`
      downloadLink.click()

      // Clean up - delay to allow tab to open
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100)

      progressItemUpdate({
        message: `📥 ${t('useGenerateImage.success.description', 'Download complete. Now you can share it with your friends and family.')}`,
        status: SUCCESSFUL,
      })
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name === 'CanceledError' || err.name === 'AbortError' || isCanceled.current) {
        return
      }

      console.error('Error fetching image:', err)
      progressItemUpdate({
        message: `${t('useGenerateImage.error.description', 'Error generating image. Please try again later.')}`,
        status: FAILED,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return { generateImage, isLoading, cancelOverlay, shouldCancel }
}
